import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Type, Search, ChevronDown, X, Monitor, Tablet, Smartphone } from 'lucide-react';
import type { KreatorSettingsData, FontConfig, ResponsiveScaling } from '../KreatorPage';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
}

// ─── Google Fonts ─────────────────────────────────────────────────────────────

import { FONT_GROUPS } from './fonts';



const FONT_SUBSETS = [
  { value: 'latin', label: 'Łacińskie' },
  { value: 'latin-ext', label: 'Łacińskie rozszerzone' },
  { value: 'cyrillic', label: 'Cyrylica' },
  { value: 'cyrillic-ext', label: 'Cyrylica rozszerzona' },
  { value: 'greek', label: 'Grecki' },
  { value: 'greek-ext', label: 'Grecki rozszerzony' },
  { value: 'vietnamese', label: 'Wietnamski' },
];

const ALL_FONT_WEIGHTS = [
  { value: 100, label: 'Cienki (100)' },
  { value: 200, label: 'Extra lekki (200)' },
  { value: 300, label: 'Lekki (300)' },
  { value: 400, label: 'Normalny (400)' },
  { value: 500, label: 'Średni (500)' },
  { value: 600, label: 'Półgruby (600)' },
  { value: 700, label: 'Gruby (700)' },
  { value: 800, label: 'Extra gruby (800)' },
  { value: 900, label: 'Czarny (900)' },
];

// Cache available weights per font family
const fontWeightsCache = new Map<string, number[]>();

async function fetchAvailableWeights(family: string): Promise<number[]> {
  if (fontWeightsCache.has(family)) return fontWeightsCache.get(family)!;
  try {
    const url = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
    const res = await fetch(url);
    const css = await res.text();
    const weights = [...new Set(
      Array.from(css.matchAll(/font-weight:\s*(\d+)/g), m => parseInt(m[1]))
    )].sort((a, b) => a - b);
    const result = weights.length > 0 ? weights : [400];
    fontWeightsCache.set(family, result);
    return result;
  } catch {
    fontWeightsCache.set(family, [400]);
    return [400];
  }
}

const TEXT_TRANSFORMS = [
  { value: 'none', label: 'Brak' },
  { value: 'uppercase', label: 'WIELKIE LITERY' },
  { value: 'lowercase', label: 'małe litery' },
  { value: 'capitalize', label: 'Pierwsze Wielkie' },
];

const DEFAULT_FONT: FontConfig = {
  family: 'Inter',
  subsets: ['latin', 'latin-ext'],
  size: 16,
  lineHeight: 1.5,
  weight: 400,
  textTransform: 'none',
  letterSpacing: 0,
};

const DEFAULT_RESPONSIVE: ResponsiveScaling = {
  smallDesktop: 100,
  tablet: 100,
  phone: 100,
};

const LOREM_IPSUM = 'Zaskakująco łatwo zbudujesz profesjonalny sklep internetowy dzięki EcomFlow. Każdy element motywu można spersonalizować, a efekt widoczny jest natychmiast.';

// ─── Google Font loader ───────────────────────────────────────────────────────

const loadedFonts = new Set<string>();

function loadGoogleFont(family: string, weights: number[] = [400, 500, 600, 700]) {
  const key = `${family}:${weights.join(',')}`;
  if (loadedFonts.has(key)) return;
  loadedFonts.add(key);

  const weightStr = weights.join(';');
  const familyParam = family.replace(/ /g, '+');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@${weightStr}&display=swap`;
  document.head.appendChild(link);
}

// ─── Font Search Dropdown ─────────────────────────────────────────────────────

function FontFamilyPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredGroups = FONT_GROUPS.map((g) => ({
    ...g,
    fonts: g.fonts.filter((f) => f.toLowerCase().includes(search.toLowerCase())),
  })).filter((g) => g.fonts.length > 0);
  const filtered = filteredGroups.flatMap((g) => g.fonts);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) filtered.forEach((f) => loadGoogleFont(f, [400]));
  }, [open, filtered]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="input w-full text-left flex items-center justify-between gap-2"
      >
        <span style={{ fontFamily: `"${value}", sans-serif` }}>{value}</span>
        <ChevronDown className={`w-4 h-4 text-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-raised border border-border rounded-lg shadow-2xl max-h-80 overflow-hidden">
          <div className="p-2 border-b border-border-subtle">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj fontu..."
                className="input w-full pl-8 pr-8 py-1.5 text-sm"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto max-h-60">
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-4 text-sm text-text-muted text-center">Nie znaleziono fontu</div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.label}>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-muted bg-surface-base/50 sticky top-0">{group.label}</div>
                  {group.fonts.map((font) => (
                    <button
                      key={font}
                      type="button"
                      onClick={() => { onChange(font); setOpen(false); setSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-hover transition-colors flex items-center justify-between ${
                        value === font ? 'text-accent-primary bg-accent-primary/5' : 'text-text-secondary'
                      }`}
                    >
                      <span style={{ fontFamily: `"${font}", sans-serif` }}>{font}</span>
                      {value === font && <span className="text-xs text-accent-primary">✓</span>}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Subset Checkboxes ────────────────────────────────────────────────────────

function SubsetPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (subset: string) => {
    if (value.includes(subset)) {
      if (value.length > 1) onChange(value.filter((s) => s !== subset));
    } else {
      onChange([...value, subset]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {FONT_SUBSETS.map((subset) => (
        <button
          key={subset.value}
          type="button"
          onClick={() => toggle(subset.value)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
            value.includes(subset.value)
              ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary'
              : 'border-border bg-surface-hover text-text-muted hover:text-text-secondary hover:border-border-strong'
          }`}
        >
          {subset.label}
        </button>
      ))}
    </div>
  );
}

// ─── Single Font Configuration Block ──────────────────────────────────────────

function FontConfigBlock({
  label,
  description,
  config,
  onChange,
}: {
  label: string;
  description: string;
  config: FontConfig;
  onChange: (config: FontConfig) => void;
}) {
  const [availableWeights, setAvailableWeights] = useState<number[]>([]);

  useEffect(() => {
    loadGoogleFont(config.family, [100, 200, 300, 400, 500, 600, 700, 800, 900]);
    fetchAvailableWeights(config.family).then(setAvailableWeights);
  }, [config.family]);

  const update = <K extends keyof FontConfig>(key: K, value: FontConfig[K]) => {
    onChange({ ...config, [key]: value });
  };

  const filteredWeights = availableWeights.length > 0
    ? ALL_FONT_WEIGHTS.filter((w) => availableWeights.includes(w.value))
    : ALL_FONT_WEIGHTS;

  return (
    <div className="card">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-text-primary">{label}</h4>
        <p className="text-xs text-text-muted mt-0.5">{description}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">Rodzina fontów</label>
          <FontFamilyPicker value={config.family} onChange={(v) => update('family', v)} />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">Grubość</label>
          <select value={config.weight} onChange={(e) => update('weight', parseInt(e.target.value))} className="input w-full">
            {filteredWeights.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">
            Wielkość <span className="text-text-disabled">({config.size}px)</span>
          </label>
          <div className="flex items-center gap-3">
            <input type="range" min={8} max={72} step={1} value={config.size} onChange={(e) => update('size', parseInt(e.target.value))} className="flex-1 accent-accent-primary" />
            <input type="number" min={8} max={120} value={config.size} onChange={(e) => { const v = e.target.value; update('size', v === '' ? 0 : parseInt(v) || config.size); }} onBlur={() => { if (!config.size || config.size < 8) update('size', 16); }} className="input w-20 text-center text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">
            Interlinia <span className="text-text-disabled">({config.lineHeight})</span>
          </label>
          <div className="flex items-center gap-3">
            <input type="range" min={0.8} max={3} step={0.05} value={config.lineHeight} onChange={(e) => update('lineHeight', parseFloat(e.target.value))} className="flex-1 accent-accent-primary" />
            <input type="number" min={0.5} max={4} step={0.05} value={config.lineHeight} onChange={(e) => { const v = e.target.value; update('lineHeight', v === '' ? 0 : parseFloat(v) || config.lineHeight); }} onBlur={() => { if (!config.lineHeight || config.lineHeight < 0.5) update('lineHeight', 1.5); }} className="input w-20 text-center text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">Transformacja tekstu</label>
          <select value={config.textTransform} onChange={(e) => update('textTransform', e.target.value as FontConfig['textTransform'])} className="input w-full">
            {TEXT_TRANSFORMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">
            Odstęp między literami <span className="text-text-disabled">({config.letterSpacing}px)</span>
          </label>
          <div className="flex items-center gap-3">
            <input type="range" min={-2} max={10} step={0.1} value={config.letterSpacing} onChange={(e) => update('letterSpacing', parseFloat(e.target.value))} className="flex-1 accent-accent-primary" />
            <input type="number" min={-5} max={20} step={0.1} value={config.letterSpacing || 0} onChange={(e) => { const v = e.target.value; update('letterSpacing', v === '' ? 0 : parseFloat(v)); }} className="input w-20 text-center text-sm" />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs text-text-secondary mb-1.5">Zestawy znaków</label>
        <SubsetPicker value={config.subsets} onChange={(v) => update('subsets', v)} />
      </div>

      {/* Live Preview */}
      <div className="mt-4 p-4 bg-surface-base rounded-lg border border-border-subtle">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider text-text-disabled">Podgląd</span>
          <span className="text-[10px] text-text-disabled">{config.family} · {config.weight} · {config.size}px</span>
        </div>
        <p
          style={{
            fontFamily: `"${config.family}", sans-serif`,
            fontSize: `${config.size}px`,
            lineHeight: config.lineHeight,
            fontWeight: config.weight,
            textTransform: config.textTransform as React.CSSProperties['textTransform'],
            letterSpacing: `${config.letterSpacing}px`,
          }}
          className="text-text-primary break-words"
        >
          {LOREM_IPSUM}
        </p>
      </div>
    </div>
  );
}

// ─── Responsive Scaling Row ───────────────────────────────────────────────────

function ResponsiveRow({
  label,
  scaling,
  onChange,
}: {
  label: string;
  scaling: ResponsiveScaling;
  onChange: (s: ResponsiveScaling) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 items-center py-2.5 border-b border-border-subtle last:border-b-0">
      <span className="text-sm text-text-secondary font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="range" min={10} max={200} value={scaling.smallDesktop}
          onChange={(e) => onChange({ ...scaling, smallDesktop: parseInt(e.target.value) })}
          className="flex-1 accent-accent-primary h-1"
        />
        <span className="text-xs text-text-secondary w-10 text-right">{scaling.smallDesktop}%</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range" min={10} max={200} value={scaling.tablet}
          onChange={(e) => onChange({ ...scaling, tablet: parseInt(e.target.value) })}
          className="flex-1 accent-accent-primary h-1"
        />
        <span className="text-xs text-text-secondary w-10 text-right">{scaling.tablet}%</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range" min={10} max={200} value={scaling.phone}
          onChange={(e) => onChange({ ...scaling, phone: parseInt(e.target.value) })}
          className="flex-1 accent-accent-primary h-1"
        />
        <span className="text-xs text-text-secondary w-10 text-right">{scaling.phone}%</span>
      </div>
    </div>
  );
}

// ─── Element Definitions ──────────────────────────────────────────────────────

type FontKey = 'logoFont' | 'menuFont' | 'dropdownLevel1Font' | 'dropdownLevel2Font'
  | 'productPriceRegularFont' | 'productPriceSaleFont' | 'productPriceUnitFont' | 'productPriceBadgeFont'
  | 'headingH1' | 'headingH2' | 'headingH3' | 'headingH4' | 'headingH5' | 'headingH6' | 'bodyFont'
  | 'pageTitleFont' | 'pageBreadcrumbsFont';
type ResponsiveKey = FontKey;

const STORE_ELEMENTS: { key: FontKey; label: string; description: string }[] = [
  { key: 'logoFont', label: 'Logo', description: 'Wyświetlany gdy brak logo graficznego w nawigacji' },
  { key: 'menuFont', label: 'Menu główne', description: 'Font elementów nawigacji' },
  { key: 'dropdownLevel1Font', label: 'Menu rozwijane - poziom 1', description: 'Font głównych pozycji w rozwijanym menu' },
  { key: 'dropdownLevel2Font', label: 'Menu rozwijane - poziom 2', description: 'Font pod-pozycji w rozwijanym menu' },
  { key: 'productPriceRegularFont', label: 'Cena regularna', description: 'Cena przekreślona oraz regularna cena produktu' },
  { key: 'productPriceSaleFont', label: 'Cena promocyjna', description: 'Aktualna cena produktu i cena promocyjna' },
  { key: 'productPriceUnitFont', label: 'Dopisek ceny jednostkowej', description: 'Tekst typu za 1 przy zakupie X przy cenie produktu' },
  { key: 'productPriceBadgeFont', label: 'Plakietki ceny', description: 'Badge rabatowy, oszczędności i oznaczenia przy cenie' },
];

const HEADING_ELEMENTS: { key: FontKey; label: string; description: string }[] = [
  { key: 'headingH1', label: 'Nagłówek H1', description: 'Główny nagłówek strony' },
  { key: 'headingH2', label: 'Nagłówek H2', description: 'Nagłówek sekcji' },
  { key: 'headingH3', label: 'Nagłówek H3', description: 'Podsekcje i bloki' },
  { key: 'headingH4', label: 'Nagłówek H4', description: 'Mniejszy nagłówek' },
  { key: 'headingH5', label: 'Nagłówek H5', description: 'Drobny nagłówek' },
  { key: 'headingH6', label: 'Nagłówek H6', description: 'Najmniejszy nagłówek' },
  { key: 'bodyFont', label: 'Tekst podstawowy', description: 'Treść akapitów i elementów inline' },
  { key: 'pageTitleFont', label: 'Nagłówek stron', description: 'Tytuł w banerze zwykłych stron WordPress' },
  { key: 'pageBreadcrumbsFont', label: 'Breadcrumbs stron', description: 'Ścieżka nawigacji pod tytułem strony' },
];

const STORE_RESPONSIVE_ELEMENTS: { key: ResponsiveKey; label: string }[] = [
  { key: 'logoFont', label: 'Logo' },
  { key: 'menuFont', label: 'Menu główne' },
  { key: 'dropdownLevel1Font', label: 'Menu poziom 1' },
  { key: 'dropdownLevel2Font', label: 'Menu poziom 2' },
  { key: 'productPriceRegularFont', label: 'Cena regularna' },
  { key: 'productPriceSaleFont', label: 'Cena promocyjna' },
  { key: 'productPriceUnitFont', label: 'Dopisek jednostkowy' },
  { key: 'productPriceBadgeFont', label: 'Plakietki ceny' },
];

const HEADING_RESPONSIVE_ELEMENTS: { key: ResponsiveKey; label: string }[] = [
  { key: 'headingH1', label: 'H1' },
  { key: 'headingH2', label: 'H2' },
  { key: 'headingH3', label: 'H3' },
  { key: 'headingH4', label: 'H4' },
  { key: 'headingH5', label: 'H5' },
  { key: 'headingH6', label: 'H6' },
  { key: 'bodyFont', label: 'Tekst' },
  { key: 'pageTitleFont', label: 'Nagłówek stron' },
  { key: 'pageBreadcrumbsFont', label: 'Breadcrumbs stron' },
];

// ─── Main Section Component ───────────────────────────────────────────────────

interface TypographyForm {
  fonts: Record<FontKey, FontConfig>;
  responsive: Record<ResponsiveKey, ResponsiveScaling>;
}

export function KreatorTypographySection({ integrationId, settings, onSaved, showError, apiAdapter }: Props) {
  const adapter = useMemo(() => apiAdapter ?? createTenantKreatorAdapter(integrationId), [apiAdapter, integrationId]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'store' | 'headings'>('store');
  const [responsiveOpen, setResponsiveOpen] = useState(false);

  const [form, setForm] = useState<TypographyForm>(() => {
    const t = settings?.typography;
    return {
      fonts: {
        logoFont: t?.logoFont ?? { ...DEFAULT_FONT, size: 24, weight: 700 },
        menuFont: t?.menuFont ?? { ...DEFAULT_FONT, size: 14, weight: 500 },
        dropdownLevel1Font: t?.dropdownLevel1Font ?? { ...DEFAULT_FONT, size: 14, weight: 500 },
        dropdownLevel2Font: t?.dropdownLevel2Font ?? { ...DEFAULT_FONT, size: 13, weight: 400 },
        productPriceRegularFont: t?.productPriceRegularFont ?? { ...DEFAULT_FONT, size: 20, weight: 600, lineHeight: 1.2 },
        productPriceSaleFont: t?.productPriceSaleFont ?? { ...DEFAULT_FONT, size: 26, weight: 700, lineHeight: 1.2 },
        productPriceUnitFont: t?.productPriceUnitFont ?? { ...DEFAULT_FONT, size: 15, weight: 700, lineHeight: 1.2 },
        productPriceBadgeFont: t?.productPriceBadgeFont ?? { ...DEFAULT_FONT, size: 14, weight: 700, lineHeight: 1.2 },
        headingH1: t?.headingH1 ?? { ...DEFAULT_FONT, size: 48, weight: 700, lineHeight: 1.2 },
        headingH2: t?.headingH2 ?? { ...DEFAULT_FONT, size: 36, weight: 700, lineHeight: 1.25 },
        headingH3: t?.headingH3 ?? { ...DEFAULT_FONT, size: 28, weight: 600, lineHeight: 1.3 },
        headingH4: t?.headingH4 ?? { ...DEFAULT_FONT, size: 22, weight: 600, lineHeight: 1.35 },
        headingH5: t?.headingH5 ?? { ...DEFAULT_FONT, size: 18, weight: 600, lineHeight: 1.4 },
        headingH6: t?.headingH6 ?? { ...DEFAULT_FONT, size: 16, weight: 600, lineHeight: 1.4 },
        bodyFont: t?.bodyFont ?? { ...DEFAULT_FONT, size: 16, weight: 400, lineHeight: 1.6 },
        pageTitleFont: t?.pageTitleFont ?? { ...DEFAULT_FONT, size: 28, weight: 700, lineHeight: 1.2 },
        pageBreadcrumbsFont: t?.pageBreadcrumbsFont ?? { ...DEFAULT_FONT, size: 15, weight: 400, lineHeight: 1.5 },
      },
      responsive: {
        headingH1: t?.responsive?.headingH1 ?? { smallDesktop: 85, tablet: 75, phone: 65 },
        headingH2: t?.responsive?.headingH2 ?? { smallDesktop: 85, tablet: 75, phone: 65 },
        headingH3: t?.responsive?.headingH3 ?? { smallDesktop: 90, tablet: 80, phone: 70 },
        headingH4: t?.responsive?.headingH4 ?? { smallDesktop: 90, tablet: 85, phone: 75 },
        headingH5: t?.responsive?.headingH5 ?? { smallDesktop: 95, tablet: 90, phone: 80 },
        headingH6: t?.responsive?.headingH6 ?? { smallDesktop: 95, tablet: 90, phone: 85 },
        bodyFont: t?.responsive?.bodyFont ?? { ...DEFAULT_RESPONSIVE },
        pageTitleFont: t?.responsive?.pageTitleFont ?? { smallDesktop: 90, tablet: 82, phone: 74 },
        pageBreadcrumbsFont: t?.responsive?.pageBreadcrumbsFont ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        logoFont: t?.responsive?.logoFont ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        menuFont: t?.responsive?.menuFont ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        dropdownLevel1Font: t?.responsive?.dropdownLevel1Font ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        dropdownLevel2Font: t?.responsive?.dropdownLevel2Font ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        productPriceRegularFont: t?.responsive?.productPriceRegularFont ?? { smallDesktop: 100, tablet: 95, phone: 85 },
        productPriceSaleFont: t?.responsive?.productPriceSaleFont ?? { smallDesktop: 100, tablet: 95, phone: 85 },
        productPriceUnitFont: t?.responsive?.productPriceUnitFont ?? { smallDesktop: 100, tablet: 95, phone: 90 },
        productPriceBadgeFont: t?.responsive?.productPriceBadgeFont ?? { smallDesktop: 100, tablet: 95, phone: 90 },
      },
    };
  });

  // Load all fonts on mount
  useEffect(() => {
    Object.values(form.fonts).forEach((cfg) => {
      loadGoogleFont(cfg.family, [100, 200, 300, 400, 500, 600, 700, 800, 900]);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await adapter.updateTypography({
        ...form.fonts,
        responsive: form.responsive,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  }, [adapter, form, onSaved, showError]);

  const updateFont = (key: FontKey, updated: FontConfig) => {
    setForm((prev) => ({ ...prev, fonts: { ...prev.fonts, [key]: updated } }));
  };

  const updateResponsive = (key: ResponsiveKey, updated: ResponsiveScaling) => {
    setForm((prev) => ({ ...prev, responsive: { ...prev.responsive, [key]: updated } }));
  };

  const elements = activeTab === 'store' ? STORE_ELEMENTS : HEADING_ELEMENTS;
  const responsiveElements = activeTab === 'store' ? STORE_RESPONSIVE_ELEMENTS : HEADING_RESPONSIVE_ELEMENTS;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Type className="w-6 h-6 text-accent-primary" />
          Typografia
        </h1>
        <p className="text-text-secondary mt-1">
          Konfiguracja fontów z Google Fonts dla poszczególnych elementów sklepu
        </p>
      </div>

      {/* Toggle Tabs */}
      <div className="flex rounded-lg bg-surface-raised border border-border-subtle p-1 w-fit">
        <button
          onClick={() => setActiveTab('store')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'store'
              ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20'
              : 'text-text-secondary hover:text-text-primary border border-transparent'
          }`}
        >
          Elementy sklepu
        </button>
        <button
          onClick={() => setActiveTab('headings')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'headings'
              ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20'
              : 'text-text-secondary hover:text-text-primary border border-transparent'
          }`}
        >
          Nagłówki i tekst
        </button>
      </div>

      {/* Font Config Blocks */}
      {elements.map((el) => (
        <FontConfigBlock
          key={el.key}
          label={el.label}
          description={el.description}
          config={form.fonts[el.key]}
          onChange={(updated) => updateFont(el.key, updated)}
        />
      ))}

      {/* Responsive Settings (collapsible) */}
      <div className="card">
        <button
          type="button"
          onClick={() => setResponsiveOpen(!responsiveOpen)}
          className="w-full flex items-start justify-between gap-4"
        >
          <SectionHeader
            icon={Monitor}
            title="Ustawienia responsywności"
            description={activeTab === 'store'
              ? 'Skalowanie elementów sklepu z tej zakładki na mniejszych ekranach'
              : 'Skalowanie nagłówków i tekstu na mniejszych ekranach'}
            accent="primary"
            className="mb-0 flex-1 text-left"
          />
          <ChevronDown className={`w-5 h-5 text-text-secondary transition-transform mt-3 flex-shrink-0 ${responsiveOpen ? 'rotate-180' : ''}`} />
        </button>

        {responsiveOpen && (
          <div className="mt-5">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 pb-3 border-b border-border">
              <span className="text-xs text-text-muted font-medium">Element</span>
              <span className="text-xs text-text-muted font-medium flex items-center gap-1.5">
                <Monitor className="w-3 h-3" /> Mały desktop
              </span>
              <span className="text-xs text-text-muted font-medium flex items-center gap-1.5">
                <Tablet className="w-3 h-3" /> Tablet
              </span>
              <span className="text-xs text-text-muted font-medium flex items-center gap-1.5">
                <Smartphone className="w-3 h-3" /> Telefon
              </span>
            </div>
            {/* Rows */}
            {responsiveElements.map((el) => (
              <ResponsiveRow
                key={el.key}
                label={el.label}
                scaling={form.responsive[el.key]}
                onChange={(s) => updateResponsive(el.key, s)}
              />
            ))}
          </div>
        )}
      </div>

      <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz typografię" />
    </div>
  );
}
